import jsLogger from 'js-logger'
import get from 'lodash/get'

const CHUNK_SIZE = 1000
const DEFAULT_VIDEO_BITRATE = 800_000
const DEFAULT_AUDIO_BITRATE = 48_000

const MIME_TYPES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
  'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
  'video/mp4'
]

// ---------- Container Guards ----------

const WEBM_MAGIC = [0x1a, 0x45, 0xdf, 0xa3]
const isWebMHeader = (buf: Uint8Array) => {
  if (buf.length < 4) return false
  return WEBM_MAGIC.every((v, i) => buf[i] === v)
}

interface ContainerGuard {
  push(chunk: Uint8Array): Uint8Array[]
  end(): Uint8Array[]
}

class WebMContainerGuard implements ContainerGuard {
  private headerSeen = false // True if EBML header has been detected
  private checkedFirstChunks = false // True once we've finished checking the first MAX_PENDING_CHUNKS
  private pending: Uint8Array[] = [] // Chunks collected before header is found
  private readonly MAX_PENDING_CHUNKS = 4
  private chunksChecked = 0 // Counter for how many chunks have been checked so far
  private _instanceId: string

  constructor(instanceId: string) {
    this._instanceId = instanceId
  }

  push = (chunk: Uint8Array): Uint8Array[] => {
    // If the initial chunks have been checked, just pass chunks through immediately
    if (this.checkedFirstChunks) {
      return [chunk]
    }

    jsLogger.log(
      `[${this._instanceId}]: WebMContainerGuard push, chunk size ${chunk.length}`
    )

    this.pending.push(chunk)
    this.chunksChecked++

    // Check if the current chunk contains the EBML header
    if (!this.headerSeen && isWebMHeader(chunk)) {
      this.headerSeen = true
      this.checkedFirstChunks = true
      jsLogger.log(
        `[${this._instanceId}]: [WebMGuard] EBML header detected in chunk ${this.chunksChecked}, size: ${chunk.length}`
      )
      // Start streaming from the header chunk, including all chunks after it
      const index = this.pending.indexOf(chunk)
      const ordered = this.pending.slice(index)
      this.pending = []
      return ordered
    }

    // If header is not found in first MAX_PENDING_CHUNKS, send everything and stop checking
    if (this.chunksChecked >= this.MAX_PENDING_CHUNKS) {
      this.checkedFirstChunks = true
      jsLogger.warn(
        `[${this._instanceId}]: [WebMGuard] EBML header not found in first ${this.MAX_PENDING_CHUNKS} chunks, sending all pending chunks`
      )
      const toSend = [...this.pending]
      this.pending = []
      return toSend
    }

    jsLogger.log(
      `[${this._instanceId}]: WebMContainerGuard: keep collecting until we find header or reach the limit, pending chunks: ${this.pending.length}`
    )
    // Otherwise, keep collecting until we find header or reach the limit
    return []
  }

  end = (): Uint8Array[] => {
    // Send any remaining pending chunks at the end
    const leftovers = [...this.pending]
    this.pending = []
    if (leftovers.length > 0) {
      jsLogger.log(
        `[${this._instanceId}]: [WebMGuard] Sending ${leftovers.length} leftover chunks at end`
      )
    }
    return leftovers
  }
}

// MP4 guard — просто накапливаем всё
class MP4ContainerGuard implements ContainerGuard {
  private chunks: Uint8Array[] = []
  private finalized = false
  private _instanceId

  constructor(instanceId: string) {
    this._instanceId = instanceId
  }

  push = (chunk: Uint8Array): Uint8Array[] => {
    if (this.finalized) return []
    this.chunks.push(chunk)
    jsLogger.log(
      `[${this._instanceId}]: [MP4Guard] Chunk received, size: ${chunk.length}, total chunks: ${this.chunks.length}`
    )
    return [chunk]
  }

  end = (): Uint8Array[] => {
    this.finalized = true
    return this.chunks
  }
}

const createContainerGuard = (
  mimeType: string,
  instanceId: string
): ContainerGuard => {
  if (mimeType.includes('webm')) return new WebMContainerGuard(instanceId)
  if (mimeType.includes('mp4')) return new MP4ContainerGuard(instanceId)
  throw new Error('Unsupported container')
}

// ---------- RVideo Class ----------

class RVideo {
  private _blobs: Blob[] = []
  private _recorder: MediaRecorder | null = null
  private _onStop: (duration: number) => void
  private _onNewChunkReady: (v: Blob) => void
  private startedAt: number = Date.now()
  private endedAt: number = Date.now()
  private _mimeType?: string = 'video/webm'
  private _verifiedMime?: string
  private _onError: (er: string) => void
  private lastDataTimestamp = Date.now()
  private livenessInterval: number | null = null
  private _bitRate: number = DEFAULT_VIDEO_BITRATE
  private _guard!: ContainerGuard
  private _instanceId = `RVideo_${Date.now()}`
  private _stream: MediaStream | null = null // for liveness track-state checks

  constructor(
    stream: MediaStream,
    onStop: (duration: number) => void,
    onNewChunkReady: (blob: Blob) => void,
    onError: (er: string) => void,
    checkLiveness: boolean,
    bitRate?: number
  ) {
    jsLogger.log(`[${this._instanceId}]: Constructor`)
    this._onStop = onStop
    this._onNewChunkReady = onNewChunkReady
    this._stream = stream
    if (bitRate) this._bitRate = bitRate

    let mimeType = ''
    for (const type of MIME_TYPES) {
      if (window.MediaRecorder && MediaRecorder.isTypeSupported(type)) {
        mimeType = type
        break
      }
    }

    if (!mimeType && /Safari/i.test(navigator.userAgent)) mimeType = 'video/mp4'

    this._onError = onError

    try {
      jsLogger.log('Rvideo: start recording with params', {
        bitRate: this._bitRate,
        mimeType,
        audioBitsPerSecond: DEFAULT_AUDIO_BITRATE
      })
      try {
        this._recorder = new MediaRecorder(stream, {
          mimeType,
          videoBitsPerSecond: this._bitRate,
          audioBitsPerSecond: DEFAULT_AUDIO_BITRATE
        })
      } catch (err) {
        console.warn(
          `[${this._instanceId}]: [getSafeRecorder] Failed to init with mimeType:`,
          {
            mimeType,
            err
          }
        )
        this._recorder = new MediaRecorder(stream, {
          videoBitsPerSecond: this._bitRate,
          audioBitsPerSecond: DEFAULT_AUDIO_BITRATE
        })
        mimeType = this._recorder.mimeType || 'unknown'
      }

      this._mimeType = mimeType
      this._guard = createContainerGuard(mimeType, this._instanceId)

      jsLogger.log(`[${this._instanceId}]: recorder initialized`, {
        mimeType: this._mimeType
      })

      this._recorder.addEventListener('stop', this._handleStop)
      this._recorder.addEventListener('error', this._handleError)
      this._recorder.addEventListener(
        'dataavailable',
        this._handleDataAvailable
      )
      this._recorder.start(CHUNK_SIZE)
      this.startedAt = Date.now()

      if (checkLiveness) this.startMonitorRecordingLiveness()
    } catch (e) {
      jsLogger.error(`[${this._instanceId}]: error:`, { e })
      this._onError(get(e, 'message', 'unknown'))
    }
  }

  private _handleDataAvailable = async (event: BlobEvent) => {
    if (event.data.size === 0) return

    const buf = new Uint8Array(await event.data.arrayBuffer())
    const chunks: Uint8Array[] = this._guard.push(buf)

    for (const c of chunks) {
      const blob = new Blob([new Uint8Array(c)], { type: this._mimeType })
      this._blobs.push(blob)
      jsLogger.log(`[${this._instanceId}]: Chunk ready`, {
        blobSize: blob.size,
        container: this._mimeType,
        verifiedMime: this._verifiedMime
      })

      if (!this._verifiedMime) {
        this._verifiedMime = await this.detectMimeType(blob)
      }
      this._onNewChunkReady(blob)
      this.lastDataTimestamp = Date.now()
    }
  }

  detectMimeType = async (blob: Blob): Promise<string> => {
    const buffer = new Uint8Array(await blob.slice(0, 16).arrayBuffer())
    const hex = Array.from(buffer)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
    if (hex.includes('1a45dfa3')) return 'video/webm'
    if (hex.includes('66747970')) return 'video/mp4'
    if (hex.includes('4f676753')) return 'video/ogg'
    return blob.type || 'application/octet-stream'
  }

  startMonitorRecordingLiveness = (): void => {
    jsLogger.log(`[${this._instanceId}]: startMonitorRecordingLiveness`)
    const checkInterval = 1000
    const timeout = 8000

    this.lastDataTimestamp = Date.now()
    // Track the real interval between ticks: when the main thread stalls (e.g. a
    // one-time WebGL shader compilation), this setInterval is starved too, so a
    // large drift means the gap below reflects the stall, not a dead camera.
    let lastTickTs = Date.now()
    this.stopMonitorRecordingLiveness()

    this.livenessInterval = window.setInterval(() => {
      if (!this._recorder || this._recorder.state !== 'recording') {
        this.stopMonitorRecordingLiveness()
        return
      }
      if (document.hidden) {
        this.lastDataTimestamp = Date.now()
        return
      }
      const now = Date.now()
      const tickDrift = now - lastTickTs
      lastTickTs = now

      if (now - this.lastDataTimestamp > timeout) {
        // Don't declare the camera dead on a wall-clock data gap that's actually
        // a main-thread stall rather than a hardware failure. If the video track
        // is still live & unmuted, or our own monitor timer was just starved
        // (tickDrift ≫ interval), the buffered data flushes once the thread frees
        // up — give the window a fresh start instead of killing the session.
        const vt = this._stream?.getVideoTracks()[0]
        const trackLive = vt ? vt.readyState === 'live' && !vt.muted : true
        const timerStarved = tickDrift > checkInterval * 2
        if (trackLive || timerStarved) {
          jsLogger.warn(
            `[${this._instanceId}]: liveness gap ignored, camera healthy`,
            {
              gapMs: now - this.lastDataTimestamp,
              tickDrift,
              trackReadyState: vt?.readyState,
              trackMuted: vt?.muted
            }
          )
          this.lastDataTimestamp = now
          return
        }
        this._onError('Camera is not available')
        this.stopMonitorRecordingLiveness()
      }
    }, checkInterval)
  }

  stopMonitorRecordingLiveness = (): void => {
    if (this.livenessInterval !== null) {
      clearInterval(this.livenessInterval)
      this.livenessInterval = null
    }
  }

  pauseRecordingLiveness = (): void => {
    jsLogger.log(`[${this._instanceId}]: pauseRecordingLiveness`)
    this.stopMonitorRecordingLiveness()
  }

  resumeRecordingLiveness = (): void => {
    jsLogger.log(`[${this._instanceId}]: resumeRecordingLiveness`)
    if (this._recorder && this._recorder.state === 'recording') {
      this.startMonitorRecordingLiveness()
    }
  }

  get blobs(): Blob[] {
    return this._blobs
  }
  get mimeType(): string | undefined {
    return this._verifiedMime || this._mimeType
  }
  get duration(): number {
    return this.endedAt - this.startedAt
  }

  stop = (): void => {
    this.stopMonitorRecordingLiveness()
    if (this._recorder) {
      this._recorder.removeEventListener('stop', this._handleStop)
      this._recorder.removeEventListener('error', this._handleError)
      this._recorder.removeEventListener(
        'dataavailable',
        this._handleDataAvailable
      )
      this._recorder.stop()
    }
  }

  private _handleStop = (): void => {
    this.stopMonitorRecordingLiveness()
    this.endedAt = Date.now()
    if (this._recorder) {
      this._recorder.stream.getTracks().forEach((track) => track.stop())
      this._recorder = null
    }
    for (const c of this._guard.end()) {
      const blob = new Blob([new Uint8Array(c)], { type: this._mimeType })
      this._blobs.push(blob)
      this._onNewChunkReady(blob)
    }
    this._onStop(this.duration)
  }

  private _handleError = (e: any): void => {
    this._onError(get(e, 'message', 'unknown'))
  }
}

export default RVideo
